import RemoveIcon from '@material-symbols/svg-300/rounded/remove.svg'
import classnames from 'classnames'
import { FieldValidator, useField } from 'formik'
import React, {
  ChangeEventHandler,
  DragEventHandler,
  ForwardedRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { mergeRefs } from 'react-merge-refs'
import Button from '../Button/Button'
import FieldError from '../FieldError/FieldError'

import styles from './FileInput.module.scss'

/**********************************************************************
 * setFileInputFiles
 **********************************************************************/
function setFileInputFiles(
  input: HTMLInputElement,
  files: File[],
  { dispatchEvent = true }: { dispatchEvent?: boolean } = {}
) {
  const list = new DataTransfer()
  for (const file of files) {
    list.items.add(file)
  }
  input.files = list.files
  if (dispatchEvent) {
    input.dispatchEvent(new Event('change', { bubbles: true }))
  }
}

/**********************************************************************
 * ControlledFileInput
 **********************************************************************/
type ControlledFileInputProps = { files: File[]; checkValidity?: (el: HTMLInputElement) => void } & Omit<
  React.ComponentPropsWithoutRef<'input'>,
  'type' | 'children' | 'value'
>

const ControlledFileInput = React.memo(
  React.forwardRef(function ControlledFileInput(
    { files, checkValidity, onChange, ...componentProps }: ControlledFileInputProps,
    ref: ForwardedRef<HTMLInputElement>
  ) {
    const inputRef = useRef<HTMLInputElement>(null)

    const [, setIteration] = useState(0)
    const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
      (e) => {
        setIteration((i) => (i += 1))
        e.target.setCustomValidity('')
        checkValidity?.(e.target)
        return onChange?.(e)
      },
      [files]
    )

    if (inputRef.current) {
      if (
        files.length !== inputRef.current.files?.length ||
        !files.every((f, i) => f === inputRef.current!.files?.[i])
      ) {
        setFileInputFiles(inputRef.current, files)

        inputRef.current.setCustomValidity('')
        checkValidity?.(inputRef.current)
      }
    }

    return <input ref={mergeRefs([ref, inputRef])} type="file" {...componentProps} onChange={handleChange} />
  })
)

/**********************************************************************
 * validateFile
 **********************************************************************/
const FILE_TOO_LARGE = 'file-too-large'
const INVALID_TYPE = 'invalid-type'
export type FileInputAccept = { description: string; accept: Record<string, string[]> }[]
function validateFile(file: File, { maxSize, types }: { maxSize?: number; types?: FileInputAccept }) {
  if (maxSize !== undefined) {
    if (file.size > maxSize) {
      return FILE_TOO_LARGE
    }
  }
  if (types) {
    const [fileBase, fileSub] = file.type.split('/')
    const isValidType = types.some(({ accept }) =>
      Object.keys(accept).some((mimeType) => {
        const [base, sub] = mimeType.split('/')
        if (base === fileBase && (sub === '*' || sub === fileSub)) {
          return true
        }
      })
    )
    if (!isValidType) {
      return INVALID_TYPE
    }
  }
}

/**********************************************************************
 * CheckedFileInput
 **********************************************************************/
type CheckedFileInputProps = { maxFiles?: number; maxSize?: number; types?: FileInputAccept } & Omit<
  React.ComponentProps<typeof ControlledFileInput>,
  'multiple' | 'accept'
>

const CheckedFileInput = React.memo(
  React.forwardRef(function CheckedFileInput(
    { maxSize, maxFiles, types, checkValidity, ...props }: CheckedFileInputProps,
    ref: ForwardedRef<HTMLInputElement>
  ) {
    const { t } = useTranslation()

    const accept = useMemo(() => {
      if (!types) {
        return undefined
      }
      const mimes = new Set()
      const extensions = new Set()
      for (const { accept } of types) {
        for (const [k, v] of Object.entries(accept)) {
          mimes.add(k)
          for (const e of v) {
            extensions.add(e)
          }
        }
      }
      return [...Array.from(mimes), ...Array.from(extensions)].join(',')
    }, [types])

    const _checkValidity = useCallback(
      (el: HTMLInputElement) => {
        const files = Array.from(el.files || [])
        if (maxFiles && files.length > maxFiles) {
          el.setCustomValidity(t('errors.fileinput.please select at most {count} files', { count: maxFiles }))
          return
        }

        const fileErrors = files.map((file) => validateFile(file, { maxSize, types })).filter(Boolean)
        if (fileErrors.length > 0) {
          const distinct = new Set(fileErrors)
          if (distinct.size === 1 && fileErrors[0] === 'file-too-large') {
            el.setCustomValidity(t('errors.fileinput.there are {count} files too large', { count: fileErrors.length }))
          } else if (distinct.size === 1 && fileErrors[0] === 'invalid-type') {
            el.setCustomValidity(
              t('errors.fileinput.there are {count} files with wrong type', { count: fileErrors.length })
            )
          } else {
            el.setCustomValidity(
              t('errors.fileinput.there are {count} inacceptible files', { count: fileErrors.length })
            )
          }
          return
        }

        checkValidity?.(el)
      },
      [maxSize, maxFiles, types, checkValidity]
    )

    return (
      <ControlledFileInput
        ref={ref}
        {...props}
        accept={accept}
        checkValidity={_checkValidity}
        multiple={maxFiles !== 1}
      />
    )
  })
)

/**********************************************************************
 * usePreventDocumentDrop
 **********************************************************************/
function usePreventDocumentDrop() {
  const capture = useCallback((e: DragEvent) => {
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'none'
    }
    e.preventDefault()
  }, [])

  useEffect(() => {
    const c = capture
    document.addEventListener('dragover', c)
    document.addEventListener('drop', c)
    return () => {
      document.removeEventListener('dragover', c)
      document.removeEventListener('drop', c)
    }
  }, [capture])
}

/**********************************************************************
 * useFileDrop
 **********************************************************************/
function useFileDrop({ onFilesDropped }: { onFilesDropped: (files: File[]) => void }) {
  const handleDragOver = useCallback<DragEventHandler>((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback<DragEventHandler>(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      onFilesDropped(Array.from(e.dataTransfer.files))
    },
    [onFilesDropped]
  )

  return { onDragOver: handleDragOver, onDrop: handleDrop }
}

/**********************************************************************
 * FileInput
 **********************************************************************/
type FileInputProps = {
  label?: string

  touched?: boolean
  error?: string
  omitErrorDisplay?: boolean
} & React.ComponentProps<typeof CheckedFileInput>

const FileInput = React.memo(
  React.forwardRef(function FileInput(
    { label, touched, error, omitErrorDisplay, ...componentProps }: FileInputProps,
    ref: ForwardedRef<HTMLInputElement>
  ) {
    const { t } = useTranslation()

    usePreventDocumentDrop()

    const inputRef = useRef<HTMLInputElement>(null)

    const handleDrop = useCallback((files: File[]) => {
      if (!inputRef.current) {
        return
      }
      setFileInputFiles(inputRef.current, [...Array.from(inputRef.current.files || []), ...files], {
        dispatchEvent: true,
      })
    }, [])

    const dropProps = useFileDrop({ onFilesDropped: handleDrop })

    const handleRemoveFile = useCallback<React.MouseEventHandler<HTMLElement>>((e) => {
      if (!inputRef.current) {
        return
      }
      const fileIdx = parseInt(e.currentTarget.dataset.fileIndex || '', 10)
      if (Number.isNaN(fileIdx)) {
        return
      }
      const files = Array.from(inputRef.current.files || [])
      files.splice(fileIdx, 1)
      setFileInputFiles(inputRef.current, files, { dispatchEvent: true })
    }, [])

    return (
      <div className={styles.FileInput} data-has-error={Boolean(touched && error) || undefined} {...dropProps}>
        <label className={styles.dropZone}>
          <div className={styles.input}>
            <CheckedFileInput ref={mergeRefs([ref, inputRef])} {...componentProps} />
          </div>
          <fieldset aria-hidden="true">
            {label && <legend>{label}</legend>}
            <span className={styles.hint}>
              {t('basic.fileinput.hint', { count: componentProps.maxFiles })}{' '}
              {componentProps.types &&
                t('basic.fileinput.accepts {types}', {
                  types: componentProps.types.map(({ description }) => description).join(', '),
                })}{' '}
              {componentProps.maxSize !== undefined &&
                t('basic.fileinput.maxsize {bytes}', { bytes: componentProps.maxSize })}
              <br />
            </span>
          </fieldset>
        </label>

        {componentProps.files.length > 0 && (
          <ul className={styles.selectedFiles}>
            {componentProps.files.map((file, idx) => {
              const error = validateFile(file, { maxSize: componentProps.maxSize, types: componentProps.types })
              return (
                <li key={idx} className={classnames({ [styles.invalidFile]: error })}>
                  <Button data-file-index={idx} onClick={handleRemoveFile} small>
                    <RemoveIcon />
                  </Button>
                  <span>
                    <span>
                      <span>{file.name}</span>
                      <span>({t('format.BYTES', { bytes: file.size })})</span>
                    </span>
                    {error && <span className={styles.fileError}>{t(`errors.fileinput.file.${error}`)}</span>}
                  </span>
                </li>
              )
            })}
          </ul>
        )}

        <FieldError error={error} touched={touched} omitErrorDisplay={omitErrorDisplay} />
      </div>
    )
  })
)

export default FileInput

/**********************************************************************
 * FileInputFormik
 **********************************************************************/
type FileInputFormikProps = { name: string; validate?: FieldValidator } & Omit<
  React.ComponentProps<typeof FileInput>,
  'name' | 'files'
>

export const FileInputFormik = React.memo(function FileInputFormik({
  name,
  validate,
  ...componentProps
}: FileInputFormikProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)

  const doValidate = useCallback(
    (value?: any) => {
      if (!inputRef.current?.validity.valid) {
        return inputRef.current?.validationMessage
      }
      if (validate) {
        return validate(value)
      }
    },
    [inputRef, validate]
  )

  const [{ value, ...field }, meta, helpers] = useField({
    name,
    validate: doValidate,
    ...componentProps,
  })

  const onChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      helpers.setValue(Array.from(e.target.files || []), true)
      helpers.setTouched(true)
    },
    [field.onChange, helpers.setValue]
  )

  return (
    <div>
      <FileInput
        ref={inputRef}
        {...componentProps}
        {...field}
        files={value}
        onChange={onChange}
        touched={meta.touched}
        error={meta.error}
      />
    </div>
  )
})
